import { useState, useRef, useCallback, useMemo, useEffect } from "react";
import {
  Box,
  Chip,
  CircularProgress,
  Collapse,
  Paper,
  Slider,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import { EGIconButton } from "../../../../../MainComponents/Button/IconButton/IconButton";
import { EGButton } from "../../../../../MainComponents/Button/EGButton/EGButton";
import { Microphone2 } from "../../../../../Icon";

export const AudioRecorderControls = ({ audioRecorder, disabled = false }) => {
  // Custom Hooks
  const theme = useTheme();
  const [t] = useTranslation("investigation");
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const [playingAudioId, setPlayingAudioId] = useState(null);
  const [audioProgress, setAudioProgress] = useState({});
  // Refs
  const audioRefs = useRef({});
  const deletedAudioRef = useRef(null);

  const {
    isRecording,
    isPaused,
    duration,
    error,
    recordedAudios,
    loadedAudios,
    isLoadingAudios,
    offlinePendingAudios,
    startRecording,
    pauseRecording,
    resumeRecording,
    stopRecording,
    deleteRecordedAudio,
    formatDuration,
  } = audioRecorder || {};

  // Surface recorder failures (mic blocked, no device…) instead of failing silently
  useEffect(() => {
    if (error) {
      enqueueSnackbar(t(`Interview.${error}`), {
        variant: "error",
        preventDuplicate: true,
      });
    }
  }, [error, enqueueSnackbar, t]);

  // Order: server-saved first (full history), then offline-pending (mid-flight),
  // then in-session recordings (newest). Each carries a `source` flag that
  // drives the row label and controls whether delete is offered.
  const allAudios = useMemo(
    () => [
      ...(loadedAudios || []),
      ...(offlinePendingAudios || []),
      ...(recordedAudios || []),
    ],
    [loadedAudios, offlinePendingAudios, recordedAudios],
  );
  const totalAudios = allAudios.length;
  const hasAnyContent = totalAudios > 0 || isLoadingAudios;

  const labelForAudio = (audio, index) => {
    const n = index + 1;
    if (audio.source === "loaded")
      return `${t("Interview.previously_saved_recording")} ${n}: `;
    if (audio.source === "offline-pending")
      return `${t("Interview.pending_upload_recording")} ${n}: `;
    if (audio.source === "recovered")
      return `${t("Interview.recovered_recording")} ${n}: `;
    return `${t("Interview.recording")} ${n}: `;
  };
  // Delete selected audio
  const handleDeleteAudio = useCallback(
    (audio, index) => {
      deletedAudioRef.current = { audio, index };
      deleteRecordedAudio?.(audio.id);

      const snackbarKey = enqueueSnackbar(t("Interview.audio_deleted"), {
        variant: "warning",
        preventDuplicate: true,
        action: () => (
          <EGButton
            label={t("Interview.undo")}
            color="warning"
            variant="outlined"
            borderColor="white"
            onClick={() => {
              if (deletedAudioRef.current && audioRecorder?.restoreAudio) {
                const { audio: deletedAudio, index: originalIndex } =
                  deletedAudioRef.current;
                audioRecorder.restoreAudio(deletedAudio, originalIndex);
                deletedAudioRef.current = null;
              }
              closeSnackbar(snackbarKey);
            }}
          />
        ),
      });
    },
    [deleteRecordedAudio, enqueueSnackbar, closeSnackbar, t, audioRecorder]
  );
  // Play and pause
  const handlePlayPause = (audioId) => {
    const audioElement = audioRefs.current[audioId];

    if (playingAudioId === audioId) {
      audioElement?.pause();
      setPlayingAudioId(null);
    } else {
      // Pause any currently playing audio
      if (playingAudioId && audioRefs.current[playingAudioId]) {
        audioRefs.current[playingAudioId].pause();
      }
      audioElement?.play();
      setPlayingAudioId(audioId);
    }
  };

  const handleAudioEnded = (audioId) => {
    if (playingAudioId === audioId) {
      setPlayingAudioId(null);
      setAudioProgress((prev) => ({ ...prev, [audioId]: 0 }));
    }
  };

  const handleTimeUpdate = (audioId) => {
    const audioElement = audioRefs.current[audioId];
    if (audioElement && audioElement.duration) {
      const progress = (audioElement.currentTime / audioElement.duration) * 100;
      setAudioProgress((prev) => ({ ...prev, [audioId]: progress }));
    }
  };

  const handleSeek = (audioId, newValue, audioDuration) => {
    const audioElement = audioRefs.current[audioId];
    if (!audioElement) return;

    // Use the element's duration if loaded, otherwise use the recorded duration
    const duration = Number.isFinite(audioElement.duration)
      ? audioElement.duration
      : audioDuration;

    if (Number.isFinite(duration) && duration > 0) {
      const newTime = (newValue / 100) * duration;
      audioElement.currentTime = newTime;
      setAudioProgress((prev) => ({ ...prev, [audioId]: newValue }));
    }
  };

  return (
    <Box
      sx={{
        width: "100%",
      }}
    >
      {/* Recording Status Indicator */}
      <Collapse in={isRecording}>
        <Paper
          elevation={0}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
            px: 2,
            mb: 0.5,
            backgroundColor: isPaused
              ? theme.palette.warning.light
              : theme.palette.error.light,
            borderRadius: 2,
            border: `1px dashed ${
              isPaused ? theme.palette.warning.dark : theme.palette.error.main
            }`,
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            {/* Recording indicator dot */}
            <Box
              sx={{
                width: 12,
                height: 12,
                borderRadius: "50%",
                backgroundColor: isPaused
                  ? theme.palette.warning.dark
                  : theme.palette.error.main,
                animation: isPaused ? "none" : "pulse 1.5s infinite",
                "@keyframes pulse": {
                  "0%": { opacity: 1 },
                  "50%": { opacity: 0.35 },
                  "100%": { opacity: 1 },
                },
              }}
            />
            <Typography
              variant="body2"
              fontWeight={600}
              color="white"
              sx={{
                textShadow: `1px 1px 1px ${
                  isPaused
                    ? theme.palette.warning.dark
                    : theme.palette.error.dark
                }`,
              }}
            >
              {isPaused
                ? t("Interview.recording_paused")
                : t("Interview.recording_in_progress")}
            </Typography>
          </Stack>

          {/* Duration display */}
          <Typography
            variant="h6"
            fontWeight={700}
            color="white"
            sx={{
              textShadow: `1px 1px 1px ${
                isPaused ? theme.palette.warning.dark : theme.palette.error.dark
              }`,
            }}
          >
            {formatDuration?.(duration) || "00:00"}
          </Typography>

          {/* Recording controls */}
          <Stack direction="row" spacing={0.5}>
            <EGIconButton
              title={
                isPaused
                  ? t("Interview.resume_recording")
                  : t("Interview.pause_recording")
              }
              onClick={isPaused ? resumeRecording : pauseRecording}
              disabled={disabled}
              iconColor="white"
              iconName={isPaused ? "bold_play_circle_3" : "bold_pause_circle"}
            />

            <EGIconButton
              title={t("Interview.stop_recording")}
              onClick={() => stopRecording?.(true)}
              disabled={disabled}
              iconColor="white"
              iconName="bold_stop_circle_2"
            />
          </Stack>
        </Paper>
      </Collapse>

      {/* Recorded Audios List */}
      <Collapse in={hasAnyContent}>
        <Paper
          elevation={1}
          sx={{
            px: 1.5,
            py: 1,
            mb: 0.5,
          }}
        >
          {totalAudios > 0 && (
            <Typography
              variant="h6"
              fontWeight={600}
              sx={{ display: "block", mb: 0.5 }}
            >
              {`${t("Interview.recorded_audio")} (${totalAudios}):`}
            </Typography>
          )}

          {isLoadingAudios && totalAudios === 0 && (
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              sx={{ py: 1, px: 0.5 }}
            >
              <CircularProgress size={16} thickness={5} />
              <Typography
                variant="body2"
                sx={{ color: theme.palette.text.secondary }}
              >
                {t("Interview.recordings_loading")}
              </Typography>
            </Stack>
          )}

          <Stack
            spacing={1}
            sx={{ maxHeight: 200, overflowY: "auto", width: "100%" }}
          >
            {allAudios.map((audio, index) => (
              <Box
                key={audio.id}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  flexFlow: "row wrap",
                  width: "100%",
                  justifyContent: "center",
                }}
              >
                {/* Hidden audio element for playback */}
                <audio
                  ref={(el) => (audioRefs.current[audio.id] = el)}
                  src={audio.url}
                  onEnded={() => handleAudioEnded(audio.id)}
                  onTimeUpdate={() => handleTimeUpdate(audio.id)}
                  style={{ display: "none" }}
                />

                <Typography fontWeight={500} mr={1} fontSize={12.5}>
                  {labelForAudio(audio, index)}
                </Typography>

                <Chip
                  size="medium"
                  label={
                    <Stack
                      direction="row"
                      alignItems="center"
                      spacing={1}
                      sx={{ minWidth: 200 }}
                    >
                      <Slider
                        size="small"
                        value={audioProgress[audio.id] || 0}
                        onChange={(e, newValue) =>
                          handleSeek(audio.id, newValue, audio.duration)
                        }
                        onClick={(e) => e.stopPropagation()}
                        sx={{
                          flex: 1,
                          minWidth: 60,
                          height: 6,
                          p: 0,
                          "& .MuiSlider-track": {
                            border: "none",
                            backgroundColor:
                              playingAudioId === audio.id
                                ? theme.palette.info.main
                                : theme.palette.success.main,
                          },
                          "& .MuiSlider-rail": {
                            backgroundColor: theme.palette.grey[300],
                          },
                          "& .MuiSlider-thumb": {
                            width: 12,
                            height: 12,
                            backgroundColor:
                              playingAudioId === audio.id
                                ? theme.palette.info.main
                                : theme.palette.success.main,
                            "&:hover, &.Mui-focusVisible": {
                              boxShadow: `0px 0px 0px 3px ${
                                playingAudioId === audio.id
                                  ? theme.palette.info.main + "30"
                                  : theme.palette.success.main + "30"
                              }`,
                            },
                          },
                        }}
                      />
                      <Typography
                        variant="body2"
                        sx={{
                          color: theme.palette.text.secondary,
                          minWidth: 75,
                          whiteSpace: "nowrap",
                          fontVariantNumeric: "tabular-nums",
                        }}
                      >
                        {`${
                          formatDuration?.(
                            ((audioProgress[audio.id] || 0) * audio.duration) /
                              100
                          ) || "00:00"
                        } / ${formatDuration?.(audio.duration) || "00:00"}`}
                      </Typography>
                    </Stack>
                  }
                  icon={
                    <EGIconButton
                      onClick={(e) => {
                        e.stopPropagation();
                        handlePlayPause(audio.id, audio.url);
                      }}
                      iconSize={16}
                      sx={{ p: 0.25 }}
                      iconColor={playingAudioId === audio.id ? "info" : "green"}
                      iconName={
                        playingAudioId === audio.id
                          ? "bold_pause"
                          : "bold_play_2"
                      }
                    />
                  }
                  onDelete={
                    audio.source === "offline-pending"
                      ? undefined
                      : () =>
                          handleDeleteAudio(
                            audio,
                            audio.source === "loaded"
                              ? index
                              : index -
                                (loadedAudios?.length || 0) -
                                (offlinePendingAudios?.length || 0),
                          )
                  }
                  deleteIcon={
                    audio.source === "offline-pending" ? undefined : (
                      <EGIconButton
                        iconName="trash_can"
                        iconColor="error"
                        iconSize={16}
                        sx={{ p: 0.25 }}
                      />
                    )
                  }
                  sx={{
                    border: `1px solid ${theme.palette.secondary.light}`,
                    justifyContent: "space-between",
                    backgroundColor: theme.palette.background.paper,
                    "& .MuiChip-label": {
                      flex: 1,
                      px: 1,
                    },
                    "& .MuiChip-icon": {
                      ml: 0.5,
                    },
                  }}
                />
              </Box>
            ))}
          </Stack>
        </Paper>
      </Collapse>

      {/* Start Recording Button (only show when not recording and no recordings yet) */}
      {!isRecording && totalAudios === 0 && !isLoadingAudios && (
        <Paper
          elevation={0}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 1,
            px: 2,
            py: 0.75,
            mb: 1.5,
            backgroundColor: theme.palette.blueGray50?.main,
            borderRadius: 2,
            border: `1px dashed ${theme.palette.primary.dark}`,
            cursor: "pointer",
            transition: "all 0.2s",
            "&:hover": {
              borderColor: theme.palette.primary.main,
              backgroundColor: theme.palette.blueGray100.main,
            },
          }}
          onClick={() => !disabled && startRecording?.()}
        >
          <Microphone2 />
          <Typography variant="h6" color="text.secondary">
            {t("Interview.start_recording")}
          </Typography>
        </Paper>
      )}

      {/* Add more recordings button (when there are already recordings and not currently recording) */}
      {!isRecording && totalAudios > 0 && (
        <Box sx={{ display: "flex", justifyContent: "center", mb: 1 }}>
          <Chip
            size="small"
            icon={<Microphone2 size={16} />}
            label={t("Interview.add_recording")}
            onClick={() => !disabled && startRecording?.()}
            disabled={disabled}
            sx={{
              backgroundColor: theme.palette.blueGray50.main,
              border: `1px dashed ${theme.palette.primary.dark}`,
              px: 1,
              py: 0.5,
              fontSize: 13,
              cursor: "pointer",
              "&:hover": {
                borderColor: theme.palette.primary.main,
                backgroundColor: theme.palette.blueGray100.main,
              },
            }}
          />
        </Box>
      )}
    </Box>
  );
};

AudioRecorderControls.displayName = "AudioRecorderControls";
